import React, { useEffect, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaEdit, FaFileAlt, FaToggleOff, FaToggleOn } from "react-icons/fa";
import { debounce } from "lodash";
import { GovPlanSecretaryProjectRoadmapItem } from "../../model/roadmap-item";
import Masks from "../../../../../../../shared/utils/mask.utils";
import { GovPlanSecretaryProject } from "../../model/project";
import { GOVERNMENT_PLAN_STATUS } from "../../../types/status";
import {
    ProjectRoadmapItemDetails,
    ProjectRoadmapItemDetailsModalHandles,
} from "../../components/roadmap-item/roadmap-item-details";
import { useInvalidateCache } from "../../../../../../../shared/hooks/useInvalidateCache";

const DraggableRow = ({
    item,
    index,
    moveRow,
    onToggle,
    onEdit,
    onView,
}: {
    item: GovPlanSecretaryProjectRoadmapItem;
    index: number;
    moveRow: (from: number, to: number) => void;
    onEdit: (item: GovPlanSecretaryProjectRoadmapItem) => void;
    onToggle: (item: GovPlanSecretaryProjectRoadmapItem) => void;
    onView: (item: GovPlanSecretaryProjectRoadmapItem) => void;
}) => {
    const [, ref] = useDrag({
        type: "row",
        item: { index },
    });

    const [, drop] = useDrop({
        accept: "row",
        hover: (draggedItem: { index: number }) => {
            if (draggedItem.index !== index) {
                moveRow(draggedItem.index, index);
                // eslint-disable-next-line no-param-reassign
                draggedItem.index = index;
            }
        },
    });

    return (
        <tr
            ref={(node) => ref(drop(node))}
            className={`${
                index % 2 === 0 ? "bg-gray-100" : "bg-gray-0 "
            } border-b hover:bg-gray-50 cursor-move`}
        >
            <td className="py-4 px-2 text-left text-teal-700 flex space-x-2">
                {item.status === GOVERNMENT_PLAN_STATUS.IN_PROGRESS && (
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<Tooltip id="button-tooltip">Editar</Tooltip>}
                    >
                        <button type="button" onClick={() => onEdit(item)}>
                            <FaEdit />
                        </button>
                    </OverlayTrigger>
                )}

                <OverlayTrigger
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                        <Tooltip id="button-tooltip">
                            {item.isCompleted()
                                ? "Marcar como em andamento"
                                : "Concluir"}
                        </Tooltip>
                    }
                >
                    <button type="button" onClick={() => onToggle(item)}>
                        {item.isCompleted() ? <FaToggleOff /> : <FaToggleOn />}
                    </button>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    overlay={<Tooltip id="button-tooltip">Detalhes</Tooltip>}
                >
                    <button type="button" onClick={() => onView(item)}>
                        <FaFileAlt />
                    </button>
                </OverlayTrigger>
            </td>
            <td className="py-4 px-2 text-left text-slate-700">{item.id}</td>
            <td className="py-4 px-2 text-left text-slate-700">{item.name}</td>
            <td className="py-4 px-2 text-left text-slate-700">
                {!!item.amount || item.amount === 0
                    ? Masks.currency(item.amount)
                    : "Não informado"}
            </td>
            <td className="py-4 px-2 text-left text-slate-700">
                <Badge bg={item.variant} className="capitalize">
                    {item.status}
                </Badge>
            </td>
            <td className="py-4 px-2 text-left text-slate-700">
                {Masks.date(item.deadline.toISOString())}
            </td>
            <td className="py-4 px-2 text-left text-slate-700">
                {Masks.date(item.createdAt.toISOString())}
            </td>
        </tr>
    );
};

const cols = [
    "#",
    "Código",
    "Nome",
    "Valor",
    "Status",
    "Data final",
    "Criado em",
];

const DragAndDropTable = ({
    project,
}: {
    project: GovPlanSecretaryProject;
}) => {
    const invalidate = useInvalidateCache();
    const detailsRef = useRef<ProjectRoadmapItemDetailsModalHandles>(null);

    const reorderDebounce = useRef(
        debounce(async (items: GovPlanSecretaryProjectRoadmapItem[]) => {
            await project.reorderRoadmap(items);
        }, 300)
    );

    const [items, setItems] = useState<GovPlanSecretaryProjectRoadmapItem[]>(
        []
    );

    useEffect(() => {
        setItems(project.roadmap);
    }, [project.roadmap]);

    const moveRow = (from: number, to: number) => {
        const updatedItems = [...items];
        const [movedItem] = updatedItems.splice(from, 1);
        updatedItems.splice(to, 0, movedItem);

        const reordered = updatedItems.map(
            (item, index) =>
                new GovPlanSecretaryProjectRoadmapItem({
                    ...item,
                    priority: index + 1,
                })
        );

        setItems(reordered);

        reorderDebounce.current(reordered);
    };

    return (
        <>
            <DndProvider backend={HTML5Backend}>
                <div className="flex flex-col w-full my-2 border rounded-lg shadow-md">
                    <div className="overflow-x-auto overflow-y-auto sm:mx-1 ">
                        <div className="inline-block min-w-full">
                            <div className="overflow-hidden">
                                <table className="my-2 w-full table-auto ">
                                    <thead className="border-b">
                                        <tr>
                                            {cols.map((it) => (
                                                <th
                                                    scope="col"
                                                    className="text-slate-600 py-4 px-2 text-left"
                                                    key={it}
                                                >
                                                    {it}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    {items.map((item, index) => (
                                        <DraggableRow
                                            key={item.id}
                                            item={item}
                                            index={index}
                                            moveRow={moveRow}
                                            onToggle={async () => {
                                                await item.toggle();
                                                invalidate();
                                            }}
                                            onEdit={(item$) =>
                                                detailsRef.current?.open({
                                                    type: "update",
                                                    projectId: item$.projectId,
                                                    project: item$,
                                                })
                                            }
                                            onView={(item$) =>
                                                detailsRef.current?.open({
                                                    type: "view",
                                                    projectId: item$.projectId,
                                                    project: item$,
                                                })
                                            }
                                        />
                                    ))}
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </DndProvider>
            <ProjectRoadmapItemDetails ref={detailsRef} />
        </>
    );
};

export default DragAndDropTable;
